import type { Method, ResponseType } from 'axios';
import { DocumentData, Firestore, Query, QueryConstraint } from 'firebase/firestore';
import { Combo } from './requests.interfaces';

export enum FirebaseProjects {
	SUITE = 'SUITE',
	API_CLIENTES = 'API_CLIENTES',
	MANTENIMIENTO_OP = 'MANTENIMIENTO_OP',
}

/** Remote Config. Login, logout y permisos se resuelven con Keycloak y variables de entorno. */
export interface FirebaseRemoteFlags {
	autenticacion: {
		host: string;
		permisos: string;
		logout: string;
	};
	'admin-suite': string;
}

export type NumberKeyData = { [key: number]: string };

export type ComboData = { [key: number]: Combo };

interface FirebaseData {
	accounts: NumberKeyData;
	combos: ComboData;
	serviceLevels: NumberKeyData;
	terminals: NumberKeyData;
}

export interface FirebaseStore extends FirebaseData {
	setAccounts(data: NumberKeyData): void;
	setCombos(data: ComboData): void;
	hosts: FirebaseRemoteFlags | null;
	setHosts(data: FirebaseRemoteFlags | null): void;
	setServiceLevels(data: NumberKeyData): void;
	setTerminals(data: NumberKeyData): void;
}

/** @deprecated Auth keys (admin-suite, permisos, logout) — usar Keycloak y REACT_APP_* */
export type FirebaseRemoteKeys = 'admin-suite' | 'host' | 'permisos' | 'logout';

export type FirebaseQueryFn = (
	project: FirebaseProjects,
	collectionName: string,
	...queryConstraints: QueryConstraint[]
) => Query<DocumentData> | undefined;

export interface FirebaseHook extends FirebaseData {
	/**
	 * Get firestore database by project.
	 *
	 * @function
	 * @returns `Firestore | undefined`
	 */
	getDatabase(project: FirebaseProjects): Firestore;

	getValue(key: FirebaseRemoteKeys): Promise<string | undefined>;
	handleQuery: FirebaseQueryFn;
	initialize(): Promise<void>;

	/**
	 * Carga los niveles de servicio desde el endpoint sifa/niveles-servicios.
	 * Esta función valida que el token esté disponible antes de hacer la petición.
	 *
	 * @async
	 * @function
	 * @returns `Promise<void>`
	 */
	loadServiceLevels(): Promise<void>;

	/**
	 * Esta función realiza una petición mediante [Axios](https://www.npmjs.com/package/axios) hacia
	 * los enlaces configurados en el Remote Config.
	 *
	 * ## Configuraciones
	 * - `body` _Body | undefined_ (Opcional)
	 * - `headers` _{ Authorization: string | undefined } | undefined_ (Opcional)
	 * - `host` _'admin-suite' | 'host' | 'permisos' | 'logout'_
	 * - `method` _'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT'_ (Opcional - Por defecto `GET`)
	 * - `responseType` _'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'_ (Opcional)
	 * - `url` _string_
	 *
	 * @async
	 * @function
	 * @param settings `FirebaseRequestConfiguration<Body>`
	 * @returns `Promise<Body>`
	 */
	request<Body>(settings: FirebaseRequestConfiguration<Body>): Promise<Body>;
}

export interface FirebaseRequestConfiguration<Body> {
	body?: Body;
	headers?: {
		Authorization?: string;
	};
	host?: FirebaseRemoteKeys;
	method?: Method;
	responseType?: ResponseType;
	url: string;
}
